import {
  ElectronicSignatureDocumentType,
  ElectronicSignatureProvider,
  PathsV1ElectronicSignatureTemplateConfigurationSearchPostParametersQueryExtend,
} from "@/api-schema-enums";
import { useCompanyGroupSearchQuery } from "@/common/api/company-group/use-company-group-search.query";
import { useElectronicSignatureTemplateConfigurationDeleteMutation } from "@/common/api/electronic-signature-template-configuration/use-electronic-signature-template-configuration-delete.mutation";
import { useElectronicSignatureTemplateConfigurationSearchQuery } from "@/common/api/electronic-signature-template-configuration/use-electronic-signature-template-configuration-search.query";
import { useElectronicSignatureTemplateConfigurationUpsertMutation } from "@/common/api/electronic-signature-template-configuration/use-electronic-signature-template-configuration-upsert.mutation";
import { handleFormSubmission } from "@/common/helpers/form-submission";
import { DeleteConfirmDialog } from "@/components/confirm-dialog/predefined/delete-confirm-dialog";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Skeleton } from "@/components/ui/skeleton";
import { PencilIcon, PlusIcon, SaveIcon, Trash2Icon, XIcon } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type ElectronicSignatureTemplateConfigurationRow,
  ELECTRONIC_SIGNATURE_TEMPLATE_ID_INPUT_PATTERN,
  EMPTY_ELECTRONIC_SIGNATURE_TEMPLATE_CONFIGURATION_CREATE_DRAFT,
  buildElectronicSignatureTemplateConfigurationCreateBody,
  buildElectronicSignatureTemplateConfigurationDeleteInput,
  buildElectronicSignatureTemplateConfigurationRows,
  buildElectronicSignatureTemplateConfigurationUpsertBody,
  buildRemainingCompanyGroupOptions,
  clearElectronicSignatureTemplateConfigurationCreateDraft,
  clearElectronicSignatureTemplateConfigurationDraft,
  getElectronicSignatureTemplateConfigurationTypeKey,
  sanitizeElectronicSignatureTemplateIdInput,
  setElectronicSignatureTemplateConfigurationCreateDraft,
  setElectronicSignatureTemplateConfigurationDraft,
} from "../_helpers/electronic-signature-template-configurations.helpers";

interface ConfigurationToDelete {
  companyGroupName: string;
  configurationUuid: string;
  draftKey: string;
}

const SUPPORTED_TEMPLATE_CONFIGURATIONS = [
  {
    provider: ElectronicSignatureProvider.Paperless,
    documentType: ElectronicSignatureDocumentType.VehicleBuyInContract,
    titleKey: "VehicleBuyInElectronicSignatureTemplate",
    descriptionKey: "VehicleBuyInElectronicSignatureTemplateDescription",
  },
] as const;

const ROW_GRID = "grid grid-cols-1 gap-3 py-2 md:grid-cols-[minmax(0,1fr)_minmax(12rem,24rem)_5.5rem] md:items-center";

export function FabElectronicSignatureTemplateConfigurations(): ReactElement {
  const { t, i18n } = useTranslation(["fab", "common", "masterdata"]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [createDrafts, setCreateDrafts] = useState<
    Record<string, typeof EMPTY_ELECTRONIC_SIGNATURE_TEMPLATE_CONFIGURATION_CREATE_DRAFT>
  >({});
  const [editingConfigurationUuid, setEditingConfigurationUuid] = useState<string | null>(null);
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const [configurationToDelete, setConfigurationToDelete] = useState<ConfigurationToDelete | null>(null);

  const companyGroupsQuery = useCompanyGroupSearchQuery({
    options: {
      body: {},
      parameters: { pageSize: -1 },
    },
  });
  const configurationsQuery = useElectronicSignatureTemplateConfigurationSearchQuery({
    options: {
      body: {
        provider: SUPPORTED_TEMPLATE_CONFIGURATIONS.map(({ provider }) => provider),
        documentType: SUPPORTED_TEMPLATE_CONFIGURATIONS.map(({ documentType }) => documentType),
      },
      parameters: {
        pageSize: -1,
        extend: [PathsV1ElectronicSignatureTemplateConfigurationSearchPostParametersQueryExtend.CompanyGroup],
      },
    },
  });
  const upsertMutation = useElectronicSignatureTemplateConfigurationUpsertMutation();
  const deleteMutation = useElectronicSignatureTemplateConfigurationDeleteMutation();

  function setDraft(key: string, value: string): void {
    setDrafts((currentDrafts) => setElectronicSignatureTemplateConfigurationDraft(currentDrafts, key, value));
  }

  function clearDraft(key: string): void {
    setDrafts((currentDrafts) => clearElectronicSignatureTemplateConfigurationDraft(currentDrafts, key));
  }

  function setCreateDraft(
    key: string,
    field: keyof typeof EMPTY_ELECTRONIC_SIGNATURE_TEMPLATE_CONFIGURATION_CREATE_DRAFT,
    value: string,
  ): void {
    setCreateDrafts((currentDrafts) => {
      const currentDraft = currentDrafts[key] ?? EMPTY_ELECTRONIC_SIGNATURE_TEMPLATE_CONFIGURATION_CREATE_DRAFT;
      return setElectronicSignatureTemplateConfigurationCreateDraft(currentDrafts, key, {
        ...currentDraft,
        [field]: value,
      });
    });
  }

  function clearCreateDraft(key: string): void {
    setCreateDrafts((currentDrafts) => clearElectronicSignatureTemplateConfigurationCreateDraft(currentDrafts, key));
  }

  function handleStartEdit(row: ElectronicSignatureTemplateConfigurationRow): void {
    setEditingConfigurationUuid(row.configuration.uuid);
    setDrafts({ [row.draftKey]: row.storedValue });
  }

  function handleCancelEdit(): void {
    setEditingConfigurationUuid(null);
    setDrafts({});
  }

  async function handleSave(row: ElectronicSignatureTemplateConfigurationRow): Promise<void> {
    const body = buildElectronicSignatureTemplateConfigurationUpsertBody(row);
    if (!body) return;

    setActiveAction(`update:${row.configuration.uuid}`);
    await handleFormSubmission(
      () => upsertMutation.mutateAsync(body),
      {
        success: t("ElectronicSignatureTemplateSaved"),
        error: t("ElectronicSignatureTemplateSaveError"),
      },
      handleCancelEdit,
    );
    setActiveAction(null);
  }

  async function handleDelete(): Promise<void> {
    if (!configurationToDelete) return;

    setActiveAction(`delete:${configurationToDelete.configurationUuid}`);
    await handleFormSubmission(
      () => deleteMutation.mutateAsync({ uuid: configurationToDelete.configurationUuid }),
      {
        success: t("ElectronicSignatureTemplateDeleted"),
        error: t("ElectronicSignatureTemplateDeleteError"),
      },
      () => {
        clearDraft(configurationToDelete.draftKey);
        setConfigurationToDelete(null);
      },
    );
    setActiveAction(null);
  }

  async function handleCreate(
    descriptorKey: string,
    provider: ElectronicSignatureProvider,
    documentType: ElectronicSignatureDocumentType,
  ): Promise<void> {
    const draft = createDrafts[descriptorKey] ?? EMPTY_ELECTRONIC_SIGNATURE_TEMPLATE_CONFIGURATION_CREATE_DRAFT;
    const body = buildElectronicSignatureTemplateConfigurationCreateBody(draft, provider, documentType);
    if (!body) return;

    setActiveAction(`create:${descriptorKey}`);
    await handleFormSubmission(
      () => upsertMutation.mutateAsync(body),
      {
        success: t("ElectronicSignatureTemplateCreated"),
        error: t("ElectronicSignatureTemplateCreateError"),
      },
      () => clearCreateDraft(descriptorKey),
    );
    setActiveAction(null);
  }

  return (
    <section className="space-y-4">
      {SUPPORTED_TEMPLATE_CONFIGURATIONS.map(({ provider, documentType, titleKey, descriptionKey }) => {
        const descriptorKey = getElectronicSignatureTemplateConfigurationTypeKey(provider, documentType);
        const rows = buildElectronicSignatureTemplateConfigurationRows(
          configurationsQuery.data?.data,
          drafts,
          provider,
          documentType,
          i18n.language,
        );
        const remainingCompanyGroupOptions = buildRemainingCompanyGroupOptions(
          companyGroupsQuery.data?.data,
          configurationsQuery.data?.data,
          provider,
          documentType,
          i18n.language,
        );
        const createDraft =
          createDrafts[descriptorKey] ?? EMPTY_ELECTRONIC_SIGNATURE_TEMPLATE_CONFIGURATION_CREATE_DRAFT;
        const createBody = buildElectronicSignatureTemplateConfigurationCreateBody(createDraft, provider, documentType);
        const isCreating = activeAction === `create:${descriptorKey}` && upsertMutation.isPending;

        return (
          <Card key={`${provider}:${documentType}`}>
            <CardHeader>
              <CardTitle>{t(titleKey)}</CardTitle>
              <CardDescription>{t(descriptionKey)}</CardDescription>
            </CardHeader>
            <CardContent>
              {configurationsQuery.isLoading && !configurationsQuery.data ? (
                <div className="space-y-3">
                  {Array.from({ length: 3 }, (_, index) => (
                    <div className={ROW_GRID} key={index}>
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-9 w-full" />
                      <div className="flex justify-end gap-2">
                        <Skeleton className="h-9 w-9 rounded-md" />
                        <Skeleton className="h-9 w-9 rounded-md" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : configurationsQuery.isError && !configurationsQuery.data ? (
                <p className="text-error text-sm">{t("ElectronicSignatureTemplatesLoadError")}</p>
              ) : (
                <div className="space-y-1">
                  {rows.length > 0 ? (
                    <div className={ROW_GRID}>
                      <p className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
                        {t("masterdata:CompanyGroup")}
                      </p>
                      <p className="text-muted-foreground text-right text-xs font-medium tracking-wider uppercase">
                        {t("ElectronicSignatureTemplateId")}
                      </p>
                      <div />
                    </div>
                  ) : null}

                  {rows.length === 0 ? (
                    <p className="text-muted-foreground py-4 text-center text-sm">
                      {t("NoElectronicSignatureTemplatesConfigured")}
                    </p>
                  ) : null}

                  {rows.map((row) => {
                    const isEditing = editingConfigurationUuid === row.configuration.uuid;
                    const isUpdating = activeAction === `update:${row.configuration.uuid}` && upsertMutation.isPending;
                    const isDeleting = activeAction === `delete:${row.configuration.uuid}` && deleteMutation.isPending;
                    const actionsDisabled = activeAction !== null;
                    const deleteInput = buildElectronicSignatureTemplateConfigurationDeleteInput(row);

                    return (
                      <div className={ROW_GRID} key={row.companyGroup.uuid}>
                        <span className="text-sm font-medium">{row.companyGroup.name}</span>
                        <div>
                          {isEditing ? (
                            <>
                              <label className="sr-only" htmlFor={`template-configuration-${row.draftKey}`}>
                                {t("ElectronicSignatureTemplateId")}
                              </label>
                              <Input
                                id={`template-configuration-${row.draftKey}`}
                                value={row.value}
                                placeholder={t("ElectronicSignatureTemplateIdPlaceholder")}
                                autoComplete="off"
                                pattern={ELECTRONIC_SIGNATURE_TEMPLATE_ID_INPUT_PATTERN}
                                inputMode="numeric"
                                disabled={isUpdating}
                                onChange={(event) =>
                                  setDraft(
                                    row.draftKey,
                                    sanitizeElectronicSignatureTemplateIdInput(provider, event.target.value),
                                  )
                                }
                              />
                            </>
                          ) : (
                            <span className="block text-right text-sm">{row.storedValue}</span>
                          )}
                        </div>
                        <div className="flex justify-end gap-2">
                          {isEditing ? (
                            <>
                              <Button
                                type="button"
                                size={ButtonSize.Sm}
                                aria-label={t("SaveElectronicSignatureTemplate", {
                                  companyGroup: row.companyGroup.name,
                                })}
                                disabled={isUpdating || !row.isDirty || !row.isValid}
                                onClick={() => void handleSave(row)}
                              >
                                {isUpdating ? (
                                  <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor />
                                ) : (
                                  <SaveIcon />
                                )}
                              </Button>
                              <Button
                                type="button"
                                size={ButtonSize.Sm}
                                variant={ButtonVariant.Outlined}
                                colorVariant={ButtonColorVariant.Secondary}
                                aria-label={t("CancelEdit")}
                                disabled={isUpdating}
                                onClick={handleCancelEdit}
                              >
                                <XIcon />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                type="button"
                                size={ButtonSize.Sm}
                                variant={ButtonVariant.Outlined}
                                colorVariant={ButtonColorVariant.Secondary}
                                aria-label={t("EditElectronicSignatureTemplate", {
                                  companyGroup: row.companyGroup.name,
                                })}
                                disabled={actionsDisabled || isDeleting}
                                onClick={() => handleStartEdit(row)}
                              >
                                <PencilIcon />
                              </Button>
                              <Button
                                type="button"
                                size={ButtonSize.Sm}
                                variant={ButtonVariant.Outlined}
                                colorVariant={ButtonColorVariant.Secondary}
                                aria-label={t("DeleteElectronicSignatureTemplate", {
                                  companyGroup: row.companyGroup.name,
                                })}
                                disabled={actionsDisabled || isDeleting}
                                onClick={() =>
                                  setConfigurationToDelete({
                                    companyGroupName: row.companyGroup.name,
                                    configurationUuid: deleteInput.uuid,
                                    draftKey: row.draftKey,
                                  })
                                }
                              >
                                {isDeleting ? (
                                  <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />
                                ) : (
                                  <Trash2Icon />
                                )}
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {companyGroupsQuery.isLoading && !companyGroupsQuery.data ? (
                    <div className={ROW_GRID}>
                      <Skeleton className="h-9 w-full" />
                      <Skeleton className="h-9 w-full" />
                      <div className="flex justify-end">
                        <Skeleton className="h-9 w-9 rounded-md" />
                      </div>
                    </div>
                  ) : companyGroupsQuery.isError && !companyGroupsQuery.data ? (
                    <p className="text-error py-2 text-sm">{t("CompanyGroupsLoadError")}</p>
                  ) : (
                    <div className={ROW_GRID}>
                      <SearchableSelect
                        id={`new-template-company-group-${descriptorKey}`}
                        options={remainingCompanyGroupOptions}
                        value={createDraft.companyGroupUuid}
                        placeholder={t("masterdata:SelectCompanyGroup")}
                        searchPlaceholder={t("common:Search")}
                        emptyText={t("AllCompanyGroupsConfigured")}
                        clearable={false}
                        disabled={activeAction !== null || remainingCompanyGroupOptions.length === 0}
                        onValueChange={(value) => setCreateDraft(descriptorKey, "companyGroupUuid", value)}
                      />
                      <div>
                        <label className="sr-only" htmlFor={`new-template-id-${descriptorKey}`}>
                          {t("ElectronicSignatureTemplateId")}
                        </label>
                        <Input
                          id={`new-template-id-${descriptorKey}`}
                          value={createDraft.templateId}
                          placeholder={t("ElectronicSignatureTemplateIdPlaceholder")}
                          autoComplete="off"
                          pattern={ELECTRONIC_SIGNATURE_TEMPLATE_ID_INPUT_PATTERN}
                          inputMode="numeric"
                          disabled={activeAction !== null || remainingCompanyGroupOptions.length === 0}
                          onChange={(event) =>
                            setCreateDraft(
                              descriptorKey,
                              "templateId",
                              sanitizeElectronicSignatureTemplateIdInput(provider, event.target.value),
                            )
                          }
                        />
                      </div>
                      <div className="flex justify-end">
                        <Button
                          type="button"
                          size={ButtonSize.Sm}
                          aria-label={t("AddElectronicSignatureTemplate")}
                          disabled={activeAction !== null || !createBody}
                          onClick={() => void handleCreate(descriptorKey, provider, documentType)}
                        >
                          {isCreating ? (
                            <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor />
                          ) : (
                            <PlusIcon />
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {configurationToDelete ? (
        <DeleteConfirmDialog
          open
          label={t("ElectronicSignatureTemplateConfiguration")}
          name={configurationToDelete.companyGroupName}
          onCancel={() => setConfigurationToDelete(null)}
          onConfirm={() => void handleDelete()}
        />
      ) : null}
    </section>
  );
}
